import { Token } from '@uniswap/sdk-core'
import { FeeAmount } from '@uniswap/v3-sdk'
import { useSingleContractMultipleData } from 'lib/hooks/multicall'
import { useMemo } from 'react'

import { useV3FactoryContract } from './useContract'

type PoolTokensData = [Token, Token, FeeAmount]

type PoolVersion = bigint

export interface PoolVersionState {
  version: PoolVersion | undefined
  loading: boolean
  valid: boolean
}

interface PoolVersionsReturnType {
  data: PoolVersionState[]
  isLoading: boolean
}

const POOL_VERSION_STATE = {
  INVALID: {
    version: undefined,
    loading: false,
    valid: false,
  } satisfies PoolVersionState,

  LOADING: {
    version: undefined,
    loading: true,
    valid: false,
  } satisfies PoolVersionState,

  EMPTY_VALID: {
    version: undefined,
    loading: false,
    valid: true,
  } satisfies PoolVersionState,

  fromVersion(version: PoolVersion): PoolVersionState {
    return {
      version,
      loading: false,
      valid: true,
    }
  },
} as const

export const usePoolVersions = (poolTokens: (PoolTokensData | undefined)[]): PoolVersionsReturnType => {
  const v3FactoryContract = useV3FactoryContract()

  const { validInputs, validIndices } = useMemo(() => {
    const validInputs: [string, string, FeeAmount][] = []
    const validIndices: number[] = []

    poolTokens.forEach((tokens, index) => {
      if (tokens) {
        const [token0, token1, fee] = tokens
        validInputs.push([token0.address, token1.address, fee])
        validIndices.push(index)
      }
    })

    return { validInputs, validIndices }
  }, [poolTokens])

  const poolVersionResults = useSingleContractMultipleData(v3FactoryContract, 'poolVersion', validInputs)

  const data = useMemo(() => {
    // Create result array with INVALID for all positions
    const results: PoolVersionState[] = new Array(poolTokens.length).fill(POOL_VERSION_STATE.INVALID)

    // Map multicall results back to original indices
    validIndices.forEach((originalIndex, resultIndex) => {
      const result = poolVersionResults[resultIndex]

      if (result?.loading) {
        results[originalIndex] = POOL_VERSION_STATE.LOADING
        return
      }

      if (!result || !result.valid || !result.result) {
        results[originalIndex] = POOL_VERSION_STATE.INVALID
        return
      }

      const versionBN = result.result[0]

      if (!versionBN || versionBN.eq(0)) {
        results[originalIndex] = POOL_VERSION_STATE.EMPTY_VALID
        return
      }

      results[originalIndex] = POOL_VERSION_STATE.fromVersion(BigInt(versionBN.toString()))
    })

    return results
  }, [poolTokens.length, poolVersionResults, validIndices])

  const versionsLoading = data.some((result) => result.loading)

  return {
    data,
    isLoading: versionsLoading,
  }
}

export const usePoolVersion = (
  token0: Token | undefined,
  token1: Token | undefined,
  fee: FeeAmount | undefined
): PoolVersionState => {
  const poolTokens = useMemo(() => {
    if (!token0 || !token1 || !fee) return [undefined]
    return [[token0, token1, fee] as PoolTokensData]
  }, [token0, token1, fee])

  const { data } = usePoolVersions(poolTokens)
  return data[0] ?? { version: undefined, loading: false, valid: false }
}
